import { useSearch } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { useCatalogo } from '@/components/mapa/catalogo'
import { useEstadoDoMapa } from '@/components/mapa/estadoDoMapa'

/*
 * `?camada={codigo}`: o observatório abre com essa camada ligada. É o link
 * "Abrir no observatório" da Área do Gestor, que mostra uma camada no ar como
 * o público a vê.
 *
 * O parâmetro só liga, e só na abertura: depois disso o estado do mapa é da
 * pessoa, que desliga a camada quando quiser, e o primeiro filtro já tira o
 * parâmetro da URL. Código que o catálogo não tem, camada ainda não entregue
 * ou catálogo em falha abrem o mapa como sempre, sem aviso nem erro.
 */

/** O parâmetro como fica na URL do mapa. */
export interface CamadaNaUrl {
  camada?: string
}

/** O formato dos códigos do catálogo (`carbono`, `vc-limites-car`, `gestao-7`). */
const CODIGO = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const TAMANHO_MAXIMO = 64

/** Lê o código da camada da URL, descartando o que não tem forma de código. */
export function lerCamadaDaUrl(bruto: Record<string, unknown>): string | undefined {
  const codigo = bruto.camada
  if (typeof codigo !== 'string' || codigo.length > TAMANHO_MAXIMO) return undefined
  return CODIGO.test(codigo) ? codigo : undefined
}

export function escreverCamadaNaUrl(codigo: string | undefined): CamadaNaUrl {
  return codigo === undefined ? {} : { camada: codigo }
}

/**
 * Liga, uma vez, a camada que a URL pediu ao abrir o observatório, assim que o
 * catálogo chega. Fica num lugar só da árvore (e não no painel, que existe
 * duas vezes: o fixo e a gaveta).
 */
export function useLigarCamadaDaUrl(): void {
  const busca: Record<string, unknown> = useSearch({ strict: false })
  // Lida na montagem: a URL que muda depois (um filtro) não liga nada de novo.
  const [pedida] = useState(() => lerCamadaDaUrl(busca))
  const catalogo = useCatalogo()
  const { despachar } = useEstadoDoMapa()
  const atendida = useRef(false)

  useEffect(() => {
    if (pedida === undefined || atendida.current || catalogo.data === undefined) return
    atendida.current = true
    const camada = catalogo.data.find((c) => c.codigo === pedida)
    // A não entregue fica de fora pelo próprio estado do mapa, que nunca a liga.
    if (camada !== undefined) despachar({ tipo: 'ligarCamada', camada })
  }, [pedida, catalogo.data, despachar])
}
